import { useEffect, useRef, useState } from "react";
import { apiCall } from "../api";

// The backdrop photo. Drop the picture into frontend/public/ under any of the names
// below, or point REACT_APP_LOGIN_BG at a hosted image URL. The first one that actually
// loads is used; if none do, the gradient underneath simply stays — never a broken
// image. Several names are accepted because the file only has to be misnamed once to
// look like the feature is broken.
const HERO_CANDIDATES = [
  process.env.REACT_APP_LOGIN_BG,
  "/shopfloor.jpg", "/shopfloor.jpeg", "/shopfloor.png",
  "/shop-floor.jpg", "/shop-floor.png",
  "/factory.jpg", "/factory.png",
  "/login-bg.jpg", "/login-bg.png",
].filter(Boolean);

// Which candidate to show. The first one is rendered immediately rather than probed
// first: a probe means nothing is requested until React has mounted, so the browser
// only learns the photo exists after the whole JS bundle has parsed. Rendering the
// <img> straight away starts the fetch at first paint, and index.html preloads the
// same file during HTML parse, so by here it is usually already in cache.
// A candidate that fails to decode simply advances the index.
function useHeroCandidate() {
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const src = index < HERO_CANDIDATES.length ? HERO_CANDIDATES[index] : null;
  const onError = () => { setLoaded(false); setIndex(i => i + 1); };
  return { src, loaded, onError, onLoad: () => setLoaded(true) };
}

// Plant-floor clock. The terminal shows IST regardless of what the device is set to —
// on a shared shop-floor machine the device clock is not something to trust.
function usePlantClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const opts = { timeZone: "Asia/Kolkata" };
  return {
    time: now.toLocaleTimeString("en-GB", { ...opts, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    date: now.toLocaleDateString("en-GB", { ...opts, day: "2-digit", month: "short", year: "numeric" }).toUpperCase(),
  };
}

const MODULES = ["PM PLAN", "BREAKDOWNS", "CHECK SHEETS", "SPARE PARTS", "REPORTS", "AUDIT TRAIL"];

function EyeIcon({ off }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
      {off ? (
        <>
          <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
          <line x1="2" x2="22" y1="2" y2="22" />
        </>
      ) : (
        <>
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

// The technical-drawing corner ticks that frame the panel.
function CornerTicks() {
  const base = "pointer-events-none absolute w-5 h-5 border-blue-400/80";
  return (
    <>
      <span className={`${base} -top-px -left-px border-t-2 border-l-2 rounded-tl-md`} aria-hidden="true" />
      <span className={`${base} -top-px -right-px border-t-2 border-r-2 rounded-tr-md`} aria-hidden="true" />
      <span className={`${base} -bottom-px -left-px border-b-2 border-l-2 rounded-bl-md`} aria-hidden="true" />
      <span className={`${base} -bottom-px -right-px border-b-2 border-r-2 rounded-br-md`} aria-hidden="true" />
    </>
  );
}

export default function LoginScreen({ onLogin }) {
  const [uname, setUname] = useState("");
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const hero = useHeroCandidate();
  const clock = usePlantClock();
  const panelRef = useRef(null);

  // Caps Lock catches people out constantly on shared shop-floor keyboards, and the
  // password field hides the evidence. getModifierState isn't in very old browsers,
  // so it is probed defensively.
  function trackCaps(e) {
    if (typeof e.getModifierState === "function") setCapsOn(e.getModifierState("CapsLock"));
  }

  async function handleSubmit(e) {
    e.preventDefault();          // a real form, so Enter signs in
    if (loading) return;
    setLoading(true);
    setErr("");
    try {
      const response = await apiCall("POST", "/auth/login", { username: uname, password: pwd });
      localStorage.setItem("token", response.token);
      if (response.refresh_token) localStorage.setItem("refreshToken", response.refresh_token);
      onLogin(response.user);
    } catch (error) {
      setErr(error.response?.data?.detail || "Invalid username or password.");
      // A short shake reads as "rejected" before anyone has finished reading the text.
      const el = panelRef.current;
      if (el) { el.classList.remove("mnt-reject"); void el.offsetWidth; el.classList.add("mnt-reject"); }
    } finally {
      setLoading(false);
    }
  }

  const fieldClass =
    "w-full rounded-md border border-white/15 bg-slate-950/60 px-3.5 py-3 text-[15px] text-white " +
    "placeholder:text-slate-600 transition outline-none focus:border-blue-500 focus:bg-slate-950/80 " +
    "focus:ring-2 focus:ring-blue-500/25";

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden bg-slate-950" data-testid="login-screen">
      <style>{`
        @keyframes mnt-drift { from { transform: scale(1.06) translate3d(0,0,0); }
                                 to { transform: scale(1.14) translate3d(-1.5%, -1%, 0); } }
        @keyframes mnt-sweep { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
        @keyframes mnt-shake { 10%,90%{transform:translateX(-2px)} 30%,70%{transform:translateX(4px)} 50%{transform:translateX(-4px)} }
        .mnt-drift { animation: mnt-drift 44s ease-in-out infinite alternate; }
        .mnt-sweep { animation: mnt-sweep 4.5s ease-in-out infinite; }
        .mnt-reject { animation: mnt-shake 0.4s ease-in-out; }
        @media (prefers-reduced-motion: reduce) {
          .mnt-drift, .mnt-sweep, .mnt-reject { animation: none; }
        }
      `}</style>

      {/* ---------- Backdrop: the shop floor, behind everything ---------- */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: "#070b14",
          backgroundImage:
            "radial-gradient(at 25% 20%, rgba(37,99,235,0.32) 0px, transparent 55%)," +
            "radial-gradient(at 80% 85%, rgba(59,130,246,0.18) 0px, transparent 50%)," +
            "linear-gradient(125deg, #070b14 0%, #131f36 55%, #070b14 100%)",
        }}
        aria-hidden="true"
      />
      {hero.src && (
        <img
          src={hero.src}
          alt=""
          aria-hidden="true"
          decoding="async"
          fetchPriority="high"
          onLoad={hero.onLoad}
          onError={hero.onError}
          className={`absolute inset-0 h-full w-full object-cover object-center mnt-drift transition-opacity duration-700 ${hero.loaded ? "opacity-100" : "opacity-0"}`}
        />
      )}
      {/* Keep the shop floor clearly visible. Only a light wash goes over the photo
          itself; the contrast the text actually needs comes from the scrims at the top
          and bottom edges, where the header and footer sit, and from the panel's own
          background. A faint blueprint grid keeps it reading as a technical surface. */}
      <div className="absolute inset-0 bg-slate-950/35" aria-hidden="true" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.10) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(148,163,184,0.10) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
        aria-hidden="true"
      />
      {/* Scrims: dark only where the header/footer text needs it, clear in the middle. */}
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom, rgba(2,6,23,0.88) 0%, rgba(2,6,23,0.10) 22%, rgba(2,6,23,0.10) 78%, rgba(2,6,23,0.88) 100%)" }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse at 50% 45%, transparent 45%, rgba(2,6,23,0.45) 100%)" }}
        aria-hidden="true"
      />

      {/* ---------- Top rail: identity left, plant clock right ---------- */}
      <header className="relative z-10 flex items-center justify-between gap-4 px-5 sm:px-8 py-5">
        <div className="flex items-center gap-3">
          <img src="/logo.jpg" alt="" className="w-9 h-9 rounded object-contain bg-white p-0.5" />
          <div className="leading-tight">
            <div className="text-[13px] font-bold text-white tracking-wide">ADVANCED ANMOL METCOMP</div>
            <div className="text-[10px] font-mono tracking-[0.22em] text-slate-500">PVT. LTD. &middot; SINCE 1996</div>
          </div>
        </div>
        <div className="text-right font-mono leading-tight">
          <div className="text-base sm:text-lg font-semibold text-blue-300 tabular-nums tracking-wider">{clock.time}</div>
          <div className="text-[10px] tracking-[0.18em] text-slate-500">{clock.date} &middot; IST</div>
        </div>
      </header>

      {/* ---------- The terminal panel ---------- */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 py-6">
        <div ref={panelRef} className="relative w-full max-w-[420px]">
          <CornerTicks />
          <div className="rounded-md border border-white/15 bg-slate-900/85 backdrop-blur-xl shadow-[0_25px_70px_-15px_rgba(0,0,0,0.9)] overflow-hidden">

            {/* Title bar — status LED + module ident, like an HMI header */}
            <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-white/[0.04] px-5 py-3">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <span className="font-mono text-[10px] tracking-[0.2em] text-emerald-300">SYSTEM ONLINE</span>
              </div>
              <span className="font-mono text-[10px] tracking-[0.2em] text-slate-500">MNT-F-01</span>
            </div>

            {/* A slow light sweep across the top edge — the panel feels powered, not static */}
            <div className="relative h-px overflow-hidden bg-white/5" aria-hidden="true">
              <div className="mnt-sweep absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-blue-400/80 to-transparent" />
            </div>

            <div className="px-5 sm:px-7 pt-7 pb-6">
              <h1 className="text-[26px] sm:text-[28px] font-bold text-white leading-none tracking-tight">
                Machine Maintenance
              </h1>
              <div className="mt-2.5 flex items-center gap-2.5">
                <span className="h-px w-7 bg-blue-500" />
                <span className="font-mono text-[10px] tracking-[0.22em] text-slate-400">OPERATOR SIGN-IN</span>
              </div>

              <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                <div>
                  <label htmlFor="login-username" className="block font-mono text-[10px] tracking-[0.18em] text-slate-400 mb-2">
                    USER ID
                  </label>
                  <input
                    id="login-username"
                    value={uname}
                    onChange={e => setUname(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    placeholder="e.g. anmolgumber"
                    className={fieldClass}
                    data-testid="login-username"
                  />
                </div>

                <div>
                  <label htmlFor="login-password" className="block font-mono text-[10px] tracking-[0.18em] text-slate-400 mb-2">
                    PASSWORD
                  </label>
                  <div className="relative">
                    <input
                      id="login-password"
                      type={showPwd ? "text" : "password"}
                      value={pwd}
                      onChange={e => setPwd(e.target.value)}
                      onKeyUp={trackCaps}
                      onKeyDown={trackCaps}
                      onBlur={() => setCapsOn(false)}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className={`${fieldClass} pr-12`}
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(s => !s)}
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      title={showPwd ? "Hide password" : "Show password"}
                      className="absolute inset-y-0 right-0 flex items-center px-3.5 text-slate-500 transition hover:text-blue-300 focus:text-blue-300 focus:outline-none"
                      data-testid="toggle-password"
                    >
                      <EyeIcon off={showPwd} />
                    </button>
                  </div>
                  {capsOn && (
                    <div className="mt-2 flex items-center gap-2 font-mono text-[10px] tracking-[0.14em] text-amber-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                      CAPS LOCK IS ON
                    </div>
                  )}
                </div>

                {err && (
                  <div
                    className="flex items-start gap-2.5 rounded-md border border-red-500/40 bg-red-950/50 px-3.5 py-2.5 text-sm text-red-200"
                    data-testid="login-error"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                    <span>{err}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full overflow-hidden rounded-md bg-blue-600 px-4 py-3.5 text-sm font-bold tracking-[0.12em] text-white transition hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="login-btn"
                >
                  {loading ? "AUTHENTICATING…" : "SIGN IN"}
                </button>
              </form>
            </div>

            {/* Foot rail — the modules this terminal opens onto */}
            <div className="border-t border-white/10 bg-slate-950/40 px-5 sm:px-7 py-3.5">
              <div className="flex flex-wrap gap-x-2.5 gap-y-1.5 font-mono text-[9px] tracking-[0.14em] text-slate-500">
                {MODULES.map((m, i) => (
                  <span key={m} className="flex items-center gap-2.5">
                    {i > 0 && <span className="text-slate-700">/</span>}
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ---------- Bottom rail ---------- */}
      <footer className="relative z-10 px-5 sm:px-8 py-4 border-t border-white/[0.07]">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 font-mono text-[10px] tracking-[0.16em] text-slate-600">
          <span>MACHINE MAINTENANCE MODULE &middot; MNT-F-01 → F-11</span>
          <span className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            SECURED CONNECTION
          </span>
        </div>
      </footer>
    </div>
  );
}
